<script setup lang="ts">
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Select from 'primevue/select'
import Textarea from 'primevue/textarea'
import ToggleSwitch from 'primevue/toggleswitch'
import Message from 'primevue/message'
import HelpToggler from '../general/HelpToggler.vue'
import type { HelpContent } from '@/store/help'

export interface DynamicSettingsOption {
  label?: string
  name?: string
  value: string | number | boolean | null
  [key: string]: unknown
}

export interface DynamicSettingsValidator {
  key: string
  message: string
  severity?: 'success' | 'info' | 'warn' | 'error' | 'secondary' | 'contrast'
  validate: (value: unknown, model: Record<string, unknown>) => boolean
}

export interface DynamicSettingsField {
  key: string
  label: string
  type?: 'text' | 'email' | 'password' | 'select' | 'textarea' | 'toggle'
  placeholder?: string
  help?: string
  disabled?: boolean
  class?: string
  inputClass?: string
  rows?: number
  options?: DynamicSettingsOption[]
  optionLabel?: string
  optionValue?: string
  required?: boolean
  requiredMessage?: string
  validators?: DynamicSettingsValidator[]
  helpID?: string
  content?: HelpContent
  [key: string]: unknown
}

const props = withDefaults(
  defineProps<{
    modelValue: Record<string, unknown>
    fields: DynamicSettingsField[]
    disabled?: boolean
    columns?: 1 | 2 | 3
    moduleString?: string
  }>(),
  {
    disabled: false,
    columns: 2,
    moduleString: undefined
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: Record<string, unknown>): void
  (e: 'field-change', payload: { key: string; value: unknown }): void
}>()

function updateField(key: string, value: unknown) {
  emit('update:modelValue', {
    ...props.modelValue,
    [key]: value
  })
  emit('field-change', { key, value })
}

function isEmpty(value: unknown) {
  return value === null || value === undefined || value === ''
}

function isValidEmail(value: unknown) {
  if (typeof value !== 'string') return false
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function getMessages(field: DynamicSettingsField) {
  const value = props.modelValue?.[field.key]
  const messages: Array<{
    key: string
    message: string
    severity: NonNullable<DynamicSettingsValidator['severity']>
  }> = []

  if (field.required && field.type !== 'toggle' && isEmpty(value)) {
    messages.push({
      key: 'required',
      message: field.requiredMessage || `${field.label} ist erforderlich.`,
      severity: 'error'
    })
  }

  if (field.type === 'email' && !isEmpty(value) && !isValidEmail(value)) {
    messages.push({
      key: 'email',
      message: 'Bitte gib eine gültige E-Mail-Adresse ein.',
      severity: 'error'
    })
  }

  field.validators?.forEach((validator) => {
    if (!validator.validate(value, props.modelValue)) {
      messages.push({
        key: validator.key,
        message: validator.message,
        severity: validator.severity || 'error'
      })
    }
  })

  return messages
}
</script>

<template>
  <div class="dynamic-settings-form-container" :data-cols="columns">
    <div class="dynamic-settings-form">
      <div v-for="field in fields" :key="field.key" class="field-wrapper" :class="field.class">
        <label :for="field.key" class="field-label">
          {{ field.label }}
          <HelpToggler v-if="field.helpID" :helpId="field.helpID" :moduleString="moduleString" />
        </label>

        <Select
          v-if="field.type === 'select'"
          :id="field.key"
          :modelValue="modelValue[field.key]"
          :options="field.options || []"
          :optionLabel="field.optionLabel || 'label'"
          :optionValue="field.optionValue || 'value'"
          :placeholder="field.placeholder"
          :disabled="disabled || field.disabled"
          appendTo="self"
          class="w-full"
          :class="field.inputClass"
          @update:modelValue="updateField(field.key, $event)"
        />

        <Textarea
          v-else-if="field.type === 'textarea'"
          :id="field.key"
          :modelValue="String(modelValue[field.key] ?? '')"
          :rows="field.rows || 4"
          :placeholder="field.placeholder"
          :disabled="disabled || field.disabled"
          class="w-full"
          :class="field.inputClass"
          @update:modelValue="updateField(field.key, $event)"
        />

        <Password
          v-else-if="field.type === 'password'"
          :inputId="field.key"
          :modelValue="String(modelValue[field.key] ?? '')"
          :placeholder="field.placeholder"
          :disabled="disabled || field.disabled"
          :feedback="false"
          toggleMask
          fluid
          :class="field.inputClass"
          @update:modelValue="updateField(field.key, $event)"
        />

        <div v-else-if="field.type === 'toggle'" class="toggle-row">
          <ToggleSwitch
            :inputId="field.key"
            :modelValue="Boolean(modelValue[field.key])"
            :disabled="disabled || field.disabled"
            @update:modelValue="updateField(field.key, $event)"
          />
        </div>

        <InputText
          v-else
          :id="field.key"
          :type="field.type || 'text'"
          :modelValue="String(modelValue[field.key] ?? '')"
          :placeholder="field.placeholder"
          :disabled="disabled || field.disabled"
          class="w-full"
          :class="field.inputClass"
          @update:modelValue="updateField(field.key, $event)"
        />

        <small v-if="field.help" class="field-help">
          {{ field.help }}
        </small>

        <div v-if="getMessages(field).length" class="field-messages">
          <Message
            v-for="message in getMessages(field)"
            :key="`${field.key}-${message.key}`"
            size="small"
            variant="simple"
            :severity="message.severity"
          >
            {{ message.message }}
          </Message>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dynamic-settings-form-container {
  container-type: inline-size;
  width: 100%;
}

.dynamic-settings-form {
  display: grid;
  gap: 1rem;
  width: 100%;
  grid-template-columns: 1fr;
}

@container (min-width: 560px) {
  .dynamic-settings-form-container[data-cols='2'] .dynamic-settings-form,
  .dynamic-settings-form-container[data-cols='3'] .dynamic-settings-form {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@container (min-width: 840px) {
  .dynamic-settings-form-container[data-cols='3'] .dynamic-settings-form {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.field-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.field-label {
  display: block;
  font-weight: 600;
  color: var(--text-color, #111827);
}

.field-help {
  color: var(--text-color-secondary, #6b7280);
  line-height: 1.45;
}

.field-messages {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.toggle-row {
  display: flex;
  align-items: center;
  min-height: 2.75rem;
}
</style>
